// Token limits for different contexts
export const TOKEN_LIMITS = {
  // Firestore document limit: ~1MB = approximately 250k tokens max
  // But we want to leave room for metadata, so be conservative
  MAX_RECIPE_TOTAL: 200000,       // Total tokens per recipe
  MAX_INGREDIENTS: 5000,          // Ingredients field
  MAX_TITLE: 100,                 // Recipe title
  MAX_NOTES: 2000,                // Recipe notes
  MAX_STEP_CONTENT: 1000,         // Single step content

  // Agent loop limits
  MAX_CONTEXT_TOKENS: 100000,     // For gpt-4 (128k limit, leave buffer)
  MAX_CONTEXT_TOKENS_GPT4_TURBO: 180000, // For gpt-4-turbo (200k limit)

  // Chat dialog limits
  MAX_CHAT_HISTORY_TOKENS: 10000, // localStorage chat history
  MAX_CHAT_MESSAGES: 50,          // Max number of messages in chat history
};

/**
 * Estimate token count for a string (rough approximation)
 * Use this for quick validation before API calls
 * For accurate counts, use the token data from OpenAI API responses
 */
export function estimateTokens(text: string): number {
  // Rough estimate: 1 token ≈ 4 characters for English text
  return Math.ceil(text.length / 4);
}

/**
 * Validate text doesn't exceed estimated token limit
 * Uses character-based estimation (fast, no tiktoken needed)
 */
export function validateTokenLimit(
  text: string,
  limit: number,
  fieldName: string
): { valid: boolean; estimatedTokens: number; error?: string } {
  const estimatedTokens = estimateTokens(text);

  if (estimatedTokens > limit) {
    return {
      valid: false,
      estimatedTokens,
      error: `${fieldName} exceeds estimated token limit: ~${estimatedTokens} tokens (max: ${limit})`,
    };
  }

  return { valid: true, estimatedTokens };
}

/**
 * Estimate token count for entire recipe
 */
export function estimateRecipeTokens(recipe: {
  title: string;
  ingredients: string;
  notes: string;
  steps?: Array<{ content: string }>;
}): number {
  let totalTokens = 0;

  totalTokens += estimateTokens(recipe.title);
  totalTokens += estimateTokens(recipe.ingredients);
  totalTokens += estimateTokens(recipe.notes);

  if (recipe.steps) {
    recipe.steps.forEach(step => {
      totalTokens += estimateTokens(step.content);
    });
  }

  return totalTokens;
}

/**
 * Prune chat message history by message count
 * Simple and fast - no token counting needed
 */
export function pruneChatHistory(
  messages: Array<{ role: string; content: string }>,
  maxMessages: number = TOKEN_LIMITS.MAX_CHAT_MESSAGES
): Array<{ role: string; content: string }> {
  if (messages.length <= maxMessages) {
    return messages;
  }

  // Always keep system message (first message) if present
  const systemMessage = messages[0]?.role === 'system' ? messages[0] : null;
  const nonSystemMessages = systemMessage ? messages.slice(1) : messages;

  // Keep the most recent messages
  const toKeep = systemMessage ? maxMessages - 1 : maxMessages;
  const kept = nonSystemMessages.slice(-toKeep);

  return systemMessage ? [systemMessage, ...kept] : kept;
}

/**
 * Prune agent message history based on actual token usage from API
 * Only call this when you have token counts from OpenAI API responses
 */
export function pruneAgentHistory(
  messages: Array<{ role: string; content: string }>,
  currentTokenCount: number,
  maxTokens: number = TOKEN_LIMITS.MAX_CONTEXT_TOKENS
): Array<{ role: string; content: string }> {
  // If we're under the limit, no need to prune
  if (currentTokenCount <= maxTokens) {
    return messages;
  }

  // Keep system message (first) and prune from the middle
  // Remove older tool results first, keeping recent context
  const systemMessage = messages[0]?.role === 'system' ? messages[0] : null;
  const otherMessages = systemMessage ? messages.slice(1) : messages;

  // Keep the most recent 60% of messages
  const keepCount = Math.floor(otherMessages.length * 0.6);
  const kept = otherMessages.slice(-keepCount);

  return systemMessage ? [systemMessage, ...kept] : kept;
}
